import React from 'react'

import PropTypes from 'prop-types'
import {Box, Flex, Grid, useMultiStyleConfig, Button, Text, Image} from '@chakra-ui/react'
import DrawerButtonCard from './drawer-button-card'
import {getGlobalLink} from '../../utils/contentstack-utils'
import {useIntl} from 'react-intl'
import {AuthHelpers, useAuthHelper, useCustomerType} from '@salesforce/commerce-sdk-react'
import {AccountIcon, LogoutIcon, FavouriteIcon} from '../icons'
import {useGigya} from '../../context/gigya'
import {getMrtApiBasepath} from '../../ssr/helpers'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {Link as RouteLink} from 'react-router-dom'
import DrawerImageCard from './drawer-image-card'
import {remCalc} from '@app/theme/_utils'

const splitPrimaryCategories = (primarycategories = []) => {
    return primarycategories.reduce(
        (acc, category) => {
            if (category?._content_type_uid === 'firstlevelsimplelink') {
                acc.simpleLinks.push(category)
            } else {
                acc.restOfCategories.push(category)
            }

            return acc
        },
        {simpleLinks: [], restOfCategories: []},
    )
}

const DrawerMenuFirstLevel = ({
    primarycategories,
    categoriesData,
    onWishlistClick,
    headerLinks,
    setSelectedPrimaryCategoryId,
    firstLevelCardData = [],
}) => {
    const styles = useMultiStyleConfig('DrawerMenu')
    const {locale} = useMultiSite()
    const intl = useIntl()
    const {isRegistered} = useCustomerType()
    const {simpleLinks, restOfCategories} = splitPrimaryCategories(primarycategories)
    const mobileImageLinks = simpleLinks.filter((category) => category?.mobile_image?.url)
    const imageCards = (firstLevelCardData ?? [])
        .map((card, idx) => ({card, idx}))
        .filter(({card}) => {
            const innerCard = card?.card
            const hasText =
                Boolean(innerCard?.title_text?.trim()) || Boolean(innerCard?.subtitle_text?.trim())
            const hasImage = ['mobile', 'tablet_768', 'tablet_1024', 'desktop'].some(
                (key) => card?.image?.[key]?.rendition && card?.image?.[key]?.dam_image?.[0]?.id,
            )
            return (card?.type === 'card' && hasText) || (card?.type === 'image' && hasImage)
        })
    const hasCards = mobileImageLinks.length > 0 || imageCards.length > 0

    const bottomButtons = [
        {
            key: 'wishlist',
            element: (
                <DrawerButtonCard
                    title={intl.formatMessage({
                        id: 'global.wishlist',
                        defaultMessage: 'Wishlist',
                    })}
                    handleClick={onWishlistClick}
                    svg={
                        <Flex {...styles.svgContainer}>
                            <FavouriteIcon {...styles.headerIconSize} />
                        </Flex>
                    }
                />
            ),
        },
        isRegistered && {
            key: 'account',
            element: (
                <DrawerButtonCard
                    title={intl.formatMessage({
                        defaultMessage: 'My Account',
                        id: 'header.popover.title.my_account',
                    })}
                    url={`/account`}
                    svg={
                        <Flex {...styles.svgContainer}>
                            <AccountIcon {...styles.headerIconSize} />
                        </Flex>
                    }
                />
            ),
        },
        ...(headerLinks ?? []).map((headerLink) => ({
            key: headerLink._metadata.uid,
            element: (
                <DrawerButtonCard
                    title={headerLink.link.title}
                    url={headerLink.link.href}
                    image={headerLink.icon}
                    isAbsolute={headerLink?.is_external_url}
                    customTitleColor={headerLink?.label_hex_color}
                    isLink
                />
            ),
        })),
        isRegistered && {key: 'logout', element: <LogoutCard />},
    ].filter(Boolean)

    // 1-2 buttons: single row; 3: 2 in first row; 4+: 3 in first row. Remaining stack full width.
    const firstRowCount =
        bottomButtons.length <= 2 ? bottomButtons.length : bottomButtons.length === 3 ? 2 : 3
    const firstRowButtons = bottomButtons.slice(0, firstRowCount)
    const restButtons = bottomButtons.slice(firstRowCount)

    return (
        <Flex
            flexDir="column"
            h={{base: 'auto', md: '100%'}}
            minH={{base: '100%', md: 0}}
            justifyContent={{base: 'flex-start', md: 'space-between'}}
            gap={{base: remCalc(137), sm: remCalc(137), md: 0}}
            pb={{base: remCalc(64), sm: remCalc(137), md: 0}}
        >
            <Grid
                templateAreas={{
                    base: hasCards ? `'cards' 'links'` : `'links'`,
                    sm: hasCards ? `'cards' 'links'` : `'links'`,
                    md: hasCards ? `'links cards'` : `'links'`,
                    lg: imageCards.length > 0 ? `'links cards'` : `'links'`,
                }}
                templateColumns={{
                    base: '1fr',
                    sm: '1fr',
                    md: hasCards ? '1fr 2fr' : '1fr',
                    lg: imageCards.length > 0 ? '1fr 2fr' : '1fr',
                }}
                gap={{
                    base: 'small.xl',
                    sm: 'small.xl',
                    md: 0,
                }}
            >
                {hasCards && (
                    <Flex
                        gridArea="cards"
                        w="100%"
                        flexDir="column"
                        display={{
                            base: 'flex',
                            lg: imageCards.length > 0 ? 'flex' : 'none',
                        }}
                        gap={{base: 'xxsmall-1.lg', md: 'xsmall.lg'}}
                    >
                        {mobileImageLinks.length > 0 && (
                            <Flex
                                display={{base: 'flex', lg: 'none'}}
                                w="100%"
                                flexDir="column"
                                gap="xsmall.base"
                            >
                                {mobileImageLinks.map((category, idx) => {
                                    const {label, url, isAbsolute} = getGlobalLink({
                                        link: category?.redirects,
                                        categoriesData: categoriesData,
                                        locale: locale?.alias,
                                    })
                                    return (
                                        <Box
                                            key={category?.uid || category?._metadata?.uid || idx}
                                            as={isAbsolute ? 'a' : RouteLink}
                                            href={isAbsolute ? url : undefined}
                                            to={!isAbsolute ? url : undefined}
                                            target={isAbsolute ? '_blank' : undefined}
                                            rel={isAbsolute ? 'noopener noreferrer' : undefined}
                                            w="100%"
                                            h={remCalc(160)}
                                            flexShrink={0}
                                            position="relative"
                                            borderRadius="large-1"
                                            overflow="hidden"
                                        >
                                            <Image
                                                src={category.mobile_image.url}
                                                alt=""
                                                w="100%"
                                                h="100%"
                                                objectFit="cover"
                                            />
                                            <Text
                                                position="absolute"
                                                top="50%"
                                                transform="translateY(-50%)"
                                                left="0"
                                                w="100%"
                                                px={remCalc(16)}
                                                textAlign="center"
                                                color={
                                                    category?.label_hex_color ||
                                                    'foreground.neutral.inverse'
                                                }
                                                textStyle="body-xxlarge"
                                            >
                                                {label}
                                            </Text>
                                        </Box>
                                    )
                                })}
                            </Flex>
                        )}
                        {imageCards.length > 0 && (
                            <Grid
                                w="100%"
                                templateColumns={{
                                    base: 'repeat(2, minmax(0, 1fr))',
                                    md: `repeat(2, ${remCalc(280)})`,
                                }}
                                gap={{
                                    base: 'xxsmall-1.lg',
                                    sm: 'xxsmall-1.lg',
                                    md: 'xsmall.lg',
                                }}
                            >
                                {imageCards.map(({card, idx}) => {
                                    return (
                                        <DrawerImageCard
                                            key={`drawer-image-card-${idx}`}
                                            imageCard={card}
                                            categoriesData={categoriesData}
                                            locale={locale}
                                        />
                                    )
                                })}
                            </Grid>
                        )}
                    </Flex>
                )}
                <Flex
                    gridArea="links"
                    w="100%"
                    flexDir="column"
                    alignItems="flex-start"
                    justifyContent="flex-start"
                    gap={{
                        base: 'xsmall.base',
                        sm: 'xsmall.base',
                        md: 'xsmall-1.base',
                    }}
                >
                    {simpleLinks?.map((category, idx) => {
                        const isCustomColor =
                            category?.label_hex_color && category?.label_hex_color !== ''
                                ? category?.label_hex_color
                                : null
                        const {label, url, isAbsolute} = getGlobalLink({
                            link: category?.redirects,
                            categoriesData: categoriesData,
                            locale: locale?.alias,
                        })
                        return (
                            <React.Fragment key={category?.uid || category?._metadata?.uid || idx}>
                                {isAbsolute ? (
                                    <Button
                                        as="a"
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="ghost"
                                        size="base"
                                        display={
                                            category?.mobile_image?.url
                                                ? {base: 'none', lg: 'inline-flex'}
                                                : undefined
                                        }
                                        padding="unset"
                                        fontWeight="light"
                                    >
                                        <Text
                                            color={
                                                isCustomColor
                                                    ? isCustomColor
                                                    : 'foreground.neutral.muted'
                                            }
                                            textStyle="body-xxlarge"
                                        >
                                            {label}
                                        </Text>
                                    </Button>
                                ) : (
                                    <Button
                                        as={RouteLink}
                                        to={url}
                                        variant="ghost"
                                        size="base"
                                        display={
                                            category?.mobile_image?.url
                                                ? {base: 'none', lg: 'inline-flex'}
                                                : undefined
                                        }
                                        padding="unset"
                                        textStyle="body-xxlarge"
                                        fontWeight="light"
                                    >
                                        <Text
                                            color={
                                                isCustomColor
                                                    ? isCustomColor
                                                    : 'foreground.neutral.muted'
                                            }
                                            textStyle="body-xxlarge"
                                            fontWeight="light"
                                        >
                                            {label}
                                        </Text>
                                    </Button>
                                )}
                            </React.Fragment>
                        )
                    })}
                    {restOfCategories?.map((category, idx) => {
                        const mainCategoryRedirect =
                            category?.primary_category?.primary_category_redirect ||
                            category?.redirects
                        const {label} = getGlobalLink({
                            link: mainCategoryRedirect,
                            categoriesData: categoriesData,
                            locale: locale?.alias,
                        })

                        const primaryCategoryColor =
                            category?.primary_category?.override_color &&
                            category?.primary_category?.override_color !== ''
                                ? category?.primary_category?.override_color
                                : null

                        return (
                            <Button
                                key={category?.uid || category?._metadata?.uid || idx}
                                onClick={() => setSelectedPrimaryCategoryId(category?.uid)}
                                variant="link-button-no-underline"
                                size="large"
                            >
                                <Text
                                    color={
                                        primaryCategoryColor
                                            ? primaryCategoryColor
                                            : 'foreground.neutral.muted'
                                    }
                                    textStyle="body-xxlarge"
                                    fontWeight="light"
                                >
                                    {label}
                                </Text>
                            </Button>
                        )
                    })}
                </Flex>
            </Grid>
            <Flex flexDir="column" gap="xsmall-1.base" data-testid="bottom-section-buttons">
                <Flex
                    gap={{
                        base: 'xxsmall-1.lg',
                        sm: 'xxsmall-1.lg',
                        md: 'xsmall.lg',
                    }}
                >
                    {firstRowButtons.map(({key, element}) => (
                        <Flex key={key} flexDir="column" flex="1 1 0" minWidth={0}>
                            {element}
                        </Flex>
                    ))}
                </Flex>
                {restButtons.length > 0 && (
                    <Flex width="100%" flexDir="column" gap="xsmall-1.base">
                        {restButtons.map(({key, element}) => (
                            <React.Fragment key={key}>{element}</React.Fragment>
                        ))}
                    </Flex>
                )}
            </Flex>
        </Flex>
    )
}

DrawerMenuFirstLevel.displayName = 'DrawerMenuFirstLevel'

DrawerMenuFirstLevel.propTypes = {
    primarycategories: PropTypes.array,
    setSelectedPrimaryCategoryId: PropTypes.func,
    categoriesData: PropTypes.object,
    onWishlistClick: PropTypes.func,
    headerLinks: PropTypes.array,
    firstLevelCardData: PropTypes.array,
}

export {DrawerMenuFirstLevel}

function LogoutCard() {
    const styles = useMultiStyleConfig('DrawerMenu')
    const intl = useIntl()
    const navigate = useNavigation()

    const {gigyaObject} = useGigya()
    const logout = useAuthHelper(AuthHelpers.Logout)

    const onSignoutClick = async () => {
        gigyaObject && gigyaObject.accounts && gigyaObject.accounts.logout()
        await logout.mutateAsync()

        const endpoint = origin + getMrtApiBasepath('/logout')
        await fetch(`${endpoint}`).then(() => {
            setTimeout(() => {
                navigate('/login')
            }, 500)
        })
    }

    return (
        <DrawerButtonCard
            title={intl.formatMessage({
                defaultMessage: 'Log out',
                id: 'header.popover.action.log_out',
            })}
            handleClick={() => onSignoutClick()}
            svg={
                <Flex {...styles.svgContainer}>
                    <LogoutIcon {...styles.headerIconSize} />
                </Flex>
            }
        />
    )
}
